import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { questionnaireService } from '../services/questionnaireService';
import { questionService } from '../services/questionService';
import {
  indicatorService,
  CreateIndicatorRequest,
  IndicatorResponse,
} from '../services/indicatorService';
import { Questionnaire, Question } from '../types';
import { ArrowLeftIcon, PlusIcon, EditIcon, TrashIcon } from '../components/Icons';
import { useToast } from '../context/ToastContext';
import { IndicatorFormModal } from '../components/IndicatorFormModal';

const IndicatorConfigPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [indicators, setIndicators] = useState<IndicatorResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingIndicator, setEditingIndicator] = useState<IndicatorResponse | null>(null);

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [questionnaireData, questionsData, indicatorsData] = await Promise.all([
        questionnaireService.getQuestionnaire(Number(id)),
        questionService.getQuestions(Number(id)),
        indicatorService.getIndicators(Number(id)),
      ]);
      setQuestionnaire(questionnaireData);
      setQuestions(questionsData);
      setIndicators(indicatorsData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      showToast('加载失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateIndicator = () => {
    setEditingIndicator(null);
    setShowModal(true);
  };

  const handleEditIndicator = (indicator: IndicatorResponse) => {
    setEditingIndicator(indicator);
    setShowModal(true);
  };

  const handleDeleteIndicator = async (indicatorId: number) => {
    if (!window.confirm('确定要删除此指标吗？')) {
      return;
    }

    try {
      await indicatorService.deleteIndicator(indicatorId);
      showToast('指标删除成功', 'success');
      fetchData();
    } catch (error) {
      console.error('Failed to delete indicator:', error);
      showToast('删除失败', 'error');
    }
  };

  const handleSaveIndicator = async (request: CreateIndicatorRequest) => {
    try {
      if (editingIndicator) {
        await indicatorService.updateIndicator(editingIndicator.id, request);
        showToast('指标更新成功', 'success');
      } else {
        await indicatorService.createIndicator(Number(id), request);
        showToast('指标创建成功', 'success');
      }
      setShowModal(false);
      fetchData();
    } catch (error: any) {
      console.error('Failed to save indicator:', error);
      showToast(error.response?.data?.message || '保存失败', 'error');
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/questionnaires/${id}`)}
          className="flex items-center text-on-surface-variant hover:text-on-surface mb-4"
        >
          <ArrowLeftIcon size={20} className="mr-2" />
          返回问卷详情
        </button>
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold font-headline text-on-surface">指标配置</h1>
            <p className="text-on-surface-variant mt-1 font-body">{questionnaire?.title}</p>
          </div>
          <button
            onClick={handleCreateIndicator}
            className="flex items-center px-4 py-2 gradient-primary text-on-primary shadow-teal rounded-xl hover:opacity-90"
          >
            <PlusIcon size={20} className="mr-2" />
            添加指标
          </button>
        </div>
      </div>

      {/* Indicators List */}
      {indicators.length === 0 ? (
        <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-8 text-center">
          <p className="text-on-surface-variant font-body">暂无指标，点击"添加指标"创建第一个指标</p>
        </div>
      ) : (
        <div className="space-y-4">
          {indicators.map((indicator) => (
            <div key={indicator.id} className="bg-surface-container-lowest rounded-2xl shadow-soft p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-on-surface">{indicator.name}</h3>
                  {indicator.description && (
                    <p className="text-on-surface-variant mt-1 font-body">{indicator.description}</p>
                  )}
                  <div className="mt-3">
                    <span className="text-sm text-on-surface-variant font-label">
                      结论规则: {indicator.conclusionRules.length} 个
                    </span>
                  </div>
                </div>
                <div className="flex space-x-2 ml-4">
                  <button
                    onClick={() => handleEditIndicator(indicator)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-xl"
                  >
                    <EditIcon size={20} />
                  </button>
                  <button
                    onClick={() => handleDeleteIndicator(indicator.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-xl"
                  >
                    <TrashIcon size={20} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <IndicatorFormModal
          indicator={editingIndicator}
          questions={questions}
          onClose={() => setShowModal(false)}
          onSave={handleSaveIndicator}
        />
      )}
    </div>
  );
};

export default IndicatorConfigPage;
